import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useDebounce } from 'use-debounce';
import { categoriesApi, type Category } from '../../api/categories';
import { formatDateIST } from '../../utils/function';
import { ActionButton } from '../common/ActionButton';
import { PageHeader } from '../common/PageHeader';
import { SearchAndFilter } from '../common/SearchAndFilter';
import ConfirmModal from '../model/ConfirmModal';
import Modal from '../model/Modal';
import { CrudActions } from '../ui/action-menu';
import { Badge } from '../ui/badge';
import { CustomPagination } from '../ui/CustomPagination';
import { CategoriesTableSkeleton } from '../ui/skeleton';
import { cn } from '../ui/utils';
import { CategoryForm } from './CategoryForm';


export default function CategoriesManager() {
    const [searchParams, setSearchParams] = useSearchParams();

    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>(
        (searchParams.get('status') as 'all' | 'active' | 'inactive') || 'all'
    );
    const page = Number(searchParams.get('page') ?? 1);
    const limit = Number(searchParams.get('limit') ?? 10);

    const [pagination, setPagination] = useState({
        total: 0,
        totalPages: 1
    });

    const [categories, setCategories] = useState<Category[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentCategory, setCurrentCategory] = useState<Category | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);

    const handleItemsPerPageChange = (newLimit: number) => {
        setPagination(prev => ({
            ...prev,
            limit: newLimit,
            page: 1
        }));
        updateSearchParams({
            limit: newLimit,
            page: 1
        });

        fetchCategories(1, searchTerm, newLimit, statusFilter);
    };

    const fetchCategories = async (page: number, search = '', limit: number, isActive?: 'all' | 'active' | 'inactive') => {
        try {
            setIsLoading(true);
            const response = await categoriesApi.getCategories({
                page,
                limit,
                search,
                status: isActive === 'active' ? 'active' : isActive === 'inactive' ? 'inactive' : undefined
            });
            if (response.success) {
                setCategories(response.data.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.data.pagination.total,
                    page: response.data.pagination.page,
                    totalPages: response.data.pagination.totalPages
                }));
            } else {
                setCategories([]);
                setPagination(prev => ({
                    ...prev,
                    total: 0,
                    page: 1,
                    totalPages: 1
                }));

            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

    const updateSearchParams = (params: {
        search?: string;
        limit?: number;
        status?: string;
        page?: number;
    }) => {
        const newParams = new URLSearchParams(searchParams);

        Object.entries(params).forEach(([key, value]) => {
            if (value === undefined || value === '' || value === 'all') {
                newParams.delete(key);
            } else {
                newParams.set(key, String(value));
            }
        });

        setSearchParams(newParams);
    };

    const handleStatusChange = (value: 'all' | 'active' | 'inactive') => {
        setStatusFilter(value);
        setPagination(prev => ({ ...prev, page: 1 }));

        updateSearchParams({
            status: value,
            page: 1
        });
    };

    useEffect(() => {
        updateSearchParams({
            search: debouncedSearchTerm,
            status: statusFilter,
            page: 1,
            limit
        });
        fetchCategories(1, debouncedSearchTerm, limit, statusFilter);
    }, [debouncedSearchTerm, limit, statusFilter]);

    const handleEdit = (category: Category) => {
        setCurrentCategory(category);
        setIsModalOpen(true);
    };

    const handleDeleteClick = (id: string) => {
        setCategoryToDelete(id);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!categoryToDelete) return;

        try {
            await categoriesApi.deleteCategory(categoryToDelete);
            toast.success('Category deleted successfully');
            fetchCategories(page, searchTerm, limit, statusFilter);
        } catch (error) {
            console.error('Error deleting category:', error);
            toast.error('Failed to delete category');
        } finally {
            setShowDeleteModal(false);
            setCategoryToDelete(null);
        }
    };

    const toggleStatus = async (id: string, currentStatus: boolean) => {
        try {
            const response = await categoriesApi.updateCategoryStatus(id, { isActive: !currentStatus });
            if (response.success) {
                if (statusFilter !== 'all') {
                    setIsLoading(false)
                    await fetchCategories(page, searchTerm, limit, statusFilter);
                } else {
                    setCategories(prevCategories =>
                        prevCategories.map(cat =>
                            cat.id === id ? { ...cat, isActive: !currentStatus } : cat
                        )
                    );
                }
                toast.success(response.message);
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            console.error('Error toggling category status:', error);
            toast.error('Failed to update category status');
        }
    };

    const resetForm = () => {
        setCurrentCategory(null);
    };

    const handlePageChange = (newPage: number) => {
        if (newPage !== page) {
            updateSearchParams({ page: newPage, limit });
            fetchCategories(newPage, searchTerm, limit, statusFilter);
        }
    };

    const ActionButtonFunction = () => {
        resetForm();
        setIsModalOpen(true);
    }

    const onSuccessFunction = (newCategory: Category) => {
        setIsModalOpen(false);
        resetForm();

        fetchCategories(page, searchTerm, limit, statusFilter);
        void newCategory;
        // if (!currentCategory) {
        //     setCategories(prev => [newCategory, ...prev]);
        // }
        // else {
        //     setCategories(prev =>
        //         prev.map(cat =>
        //             cat.id === newCategory.id ? newCategory : cat
        //         )
        //     );
        // }
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Categories"
                description="Manage your radio station categories."
                action={
                    <ActionButton
                        onClick={ActionButtonFunction}
                        icon={<Plus className="w-4 h-4" />}
                    >
                        Add New Category
                    </ActionButton>
                }
            />

            <SearchAndFilter
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onClearSearch={() => setSearchTerm('')}
                statusFilter={statusFilter}
                onStatusChange={handleStatusChange}
                filterOptions={[
                    { value: "all", label: "All Categories" },
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                ]}
                searchPlaceholder="Search categories by name..."
                filterPlaceholder="Filter categories..."
            />

            <div className="bg-[#0B1623] border border-white/10 rounded-2xl">
                <div className="overflow-x-auto custom-scrollbar">
                    <table className="w-full">
                        <thead className="bg-white/5 border-b border-white/10">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Category Name</th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Updated At</th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Created At</th>
                                <th className="px-6 py-4 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {isLoading ? (
                                <CategoriesTableSkeleton />
                            ) : categories.length > 0 ? (
                                categories?.map((category) => (
                                    <tr key={category?.id} className="hover:bg-white/2 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-white group-hover:text-cyan-300 transition-colors">
                                                {category?.name ?? "-"}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge
                                                variant="outline"
                                                className={cn(
                                                    "rounded-full text-xs font-medium cursor-pointer",
                                                    category?.isActive
                                                        ? 'bg-green-500/20 text-green-400 border-green-500/30 hover:bg-green-500/30'
                                                        : 'bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30'
                                                )}
                                                onClick={() => toggleStatus(category?.id, category?.isActive)}
                                            >
                                                {category?.isActive ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-300">
                                            {formatDateIST(category?.updatedAt)}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-300">
                                            {formatDateIST(category?.createdAt)}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <CrudActions
                                                onEdit={() => handleEdit(category)}
                                                onDelete={() => handleDeleteClick(category?.id)}
                                            />
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-slate-400">
                                        {searchTerm || statusFilter !== 'all'
                                            ? 'No categories match your search criteria.'
                                            : 'No categories found. Get started by adding a new category.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    <CustomPagination
                        currentPage={page}
                        totalItems={pagination.total}
                        itemsPerPage={limit}
                        totalPages={pagination.totalPages}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        itemsPerPageOptions={[5, 10, 25, 50]}  // Optional: customize the options
                    />
                </div>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    resetForm();
                }}
                title={currentCategory ? 'Edit Category' : 'Add New Category'}
            >
                <CategoryForm
                    initialData={currentCategory || { name: '', isActive: true }}
                    onSuccess={onSuccessFunction}
                    onCancel={() => {
                        setIsModalOpen(false);
                        resetForm();
                    }}
                />
            </Modal>

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Category"
                message="Are you sure you want to delete this category? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
            />
        </div>
    );
}